// Terms of use (backend terms.py / routers/terms.py; text in
// config/terms_of_use.yaml). Three dialogs, one component:
//
//   accept    blocking: shown after login, and on app load, while /me says
//             the current version isn't accepted (the backend 403s every
//             other /api route until it is). Scrollable terms, an "I have
//             read and agree" checkbox, "Accept and continue" (disabled
//             until ticked) and "Decline and log out".
//   reminder  blocking: shown once per NEW login when the version is already
//             accepted: the short summary, "View full terms", "I understand".
//             A page reload in the same session doesn't show it again
//             (sessions.terms_ack_at).
//   view      read-only, closable: the sidebar footer's "Terms of use" link.
//
// Accessible: role="dialog" + aria-modal, focus moves in and is trapped
// (Tab / Shift+Tab cycle), the rest of the page is inert, Escape closes only
// the read-only view, and focus returns to the opener. Plain text only
// (textContent), never innerHTML.
import { acceptTerms, acknowledgeTerms, getTerms, logout, setTermsRequiredHandler } from "./api";
import { hideBootError, offlineText, showBootError } from "./bootError";
import { termsBootAction, withRetry } from "./bootRetry";
import { el } from "./legend";
import { getState, setState } from "./state";
import type { TermsDoc } from "./types";

type Mode = "accept" | "reminder" | "view";

let openOverlay: HTMLElement | null = null;

function setPageInert(on: boolean): void {
  for (const id of ["app", "login-screen"]) {
    const node = document.getElementById(id);
    if (!node) continue;
    if (on) node.setAttribute("inert", "");
    else node.removeAttribute("inert");
  }
}

/** Body text -> paragraphs and bullet lists ("- " lines), as DOM nodes. */
function renderBody(body: string): HTMLElement[] {
  const out: HTMLElement[] = [];
  for (const block of body.split(/\n\s*\n/)) {
    let list: HTMLUListElement | null = null;
    for (const raw of block.split("\n")) {
      const line = raw.trim();
      if (!line) continue;
      if (line.startsWith("- ")) {
        if (!list) {
          list = el("ul");
          out.push(list);
        }
        list.append(el("li", {}, line.slice(2)));
      } else {
        list = null;
        out.push(el("p", {}, line));
      }
    }
  }
  return out;
}

function renderFullTerms(doc: TermsDoc): HTMLElement {
  const wrap = el("div", { className: "terms-body" });
  wrap.tabIndex = 0; // scrollable region reachable by keyboard
  wrap.setAttribute("role", "region");
  wrap.setAttribute("aria-label", "Terms of use text");
  for (const section of doc.sections) {
    wrap.append(el("h4", {}, section.heading), ...renderBody(section.body));
  }
  return wrap;
}

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((n) => n.offsetParent !== null || n === document.activeElement);
}

/** Show one dialog; resolves when it closes (accepted / acknowledged /
 * closed). Decline logs out and reloads, so it never resolves. */
function showDialog(doc: TermsDoc, mode: Mode): Promise<void> {
  openOverlay?.remove();
  const opener = document.activeElement as HTMLElement | null;

  return new Promise<void>((resolve) => {
    const overlay = el("div", { className: "terms-overlay" });
    const modal = el("div", { className: "terms-modal" });
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-labelledby", "terms-dialog-title");
    overlay.append(modal);

    const title = el("h3", { id: "terms-dialog-title" }, mode === "reminder" ? `Reminder: ${doc.title}` : doc.title);
    const meta = el(
      "p",
      { className: "terms-meta" },
      `Version ${doc.version}${doc.effective_date ? ` · effective ${doc.effective_date}` : ""}`,
    );
    modal.append(title, meta);
    if (doc.draft_notice) modal.append(el("p", { className: "terms-draft" }, doc.draft_notice));

    const errorEl = el("p", { className: "terms-error" });
    errorEl.setAttribute("aria-live", "polite");
    const actions = el("div", { className: "terms-actions" });

    const close = () => {
      document.removeEventListener("keydown", onKey, true);
      overlay.remove();
      if (openOverlay === overlay) openOverlay = null;
      setPageInert(false);
      opener?.focus?.();
      resolve();
    };

    let initialFocus: HTMLElement;

    if (mode === "accept") {
      const body = renderFullTerms(doc);
      const check = el("input", { type: "checkbox", id: "terms-agree" }) as HTMLInputElement;
      const label = el("label", { className: "terms-check", htmlFor: "terms-agree" });
      label.append(check, document.createTextNode(" I have read and agree to these terms"));
      const decline = el("button", { type: "button", className: "terms-secondary" }, "Decline and log out");
      const accept = el("button", { type: "button", className: "terms-primary", disabled: true }, "Accept and continue");
      check.addEventListener("change", () => {
        accept.disabled = !check.checked;
      });
      decline.addEventListener("click", () => {
        decline.disabled = accept.disabled = true;
        void logout().finally(() => window.location.reload());
      });
      accept.addEventListener("click", () => {
        accept.disabled = true;
        errorEl.textContent = "";
        acceptTerms(doc.version)
          .then((status) => {
            const user = getState().currentUser;
            if (user) setState({ currentUser: { ...user, ...status } });
            close();
          })
          .catch((err: unknown) => {
            const changed = err instanceof Error && err.message.includes("terms_version_changed");
            if (changed) {
              // The terms were updated while this dialog was open: show the new text.
              void getTerms().then((fresh) => {
                overlay.remove();
                openOverlay = null;
                void showDialog(fresh, "accept").then(resolve);
              });
              return;
            }
            errorEl.textContent = "Could not record your acceptance. Please try again.";
            accept.disabled = !check.checked;
          });
      });
      actions.append(decline, accept);
      modal.append(body, label, errorEl, actions);
      initialFocus = body;
    } else if (mode === "reminder") {
      const intro = el("p", {}, "Before you continue, please remember:");
      const list = el("ul", { className: "terms-summary" });
      for (const line of doc.summary) list.append(el("li", {}, line));
      const full = renderFullTerms(doc);
      full.hidden = true;
      const toggle = el("button", { type: "button", className: "terms-secondary" }, "View full terms");
      toggle.setAttribute("aria-expanded", "false");
      toggle.addEventListener("click", () => {
        full.hidden = !full.hidden;
        toggle.setAttribute("aria-expanded", String(!full.hidden));
        toggle.textContent = full.hidden ? "View full terms" : "Hide full terms";
        if (!full.hidden) full.focus();
      });
      const ok = el("button", { type: "button", className: "terms-primary" }, "I understand");
      ok.addEventListener("click", () => {
        ok.disabled = true;
        // Best effort: the reminder is a UI step, not a server gate.
        acknowledgeTerms()
          .then((status) => {
            const user = getState().currentUser;
            if (user) setState({ currentUser: { ...user, ...status } });
          })
          .catch(() => undefined)
          .finally(close);
      });
      actions.append(toggle, ok);
      modal.append(intro, list, full, errorEl, actions);
      initialFocus = ok;
    } else {
      const body = renderFullTerms(doc);
      const closeBtn = el("button", { type: "button", className: "terms-primary" }, "Close");
      closeBtn.addEventListener("click", close);
      actions.append(closeBtn);
      modal.append(body, actions);
      initialFocus = body;
    }

    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape" && mode === "view") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusables(modal);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (!active || !modal.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKey, true);
    document.body.append(overlay);
    openOverlay = overlay;
    setPageInert(true);
    initialFocus.focus();
  });
}

/** /api/terms with retries (a request lost in a Wi-Fi -> mobile data
 * hand-over, 2026-10-08). */
function fetchTerms(attempts = 4): Promise<TermsDoc> {
  return withRetry(() => getTerms(), { attempts, baseMs: 500, maxMs: 4000 });
}

// The once-per-login reminder when its text couldn't be loaded at boot: the
// server only enforces ACCEPTANCE (terms.py TermsMiddleware), so the app
// boots and the reminder comes up later - when the signal is back, the app
// is shown again, or on a slow timer - without ever blocking work.
const DEFERRED_RETRY_MS = [30_000, 120_000, 300_000];
let deferredArmed = false;

function deferReminder(): void {
  if (deferredArmed) return;
  deferredArmed = true;
  let tries = 0;
  let timer: number | null = null;
  let running = false;
  const stop = () => {
    if (timer !== null) window.clearTimeout(timer);
    window.removeEventListener("online", attempt);
    document.removeEventListener("visibilitychange", onVisible);
    deferredArmed = false;
  };
  const schedule = () => {
    if (timer !== null) window.clearTimeout(timer);
    timer = tries < DEFERRED_RETRY_MS.length ? window.setTimeout(attempt, DEFERRED_RETRY_MS[tries]) : null;
  };
  async function attempt(): Promise<void> {
    if (running || !navigator.onLine) return;
    running = true;
    tries++;
    try {
      const doc = await fetchTerms(2);
      stop();
      if (!doc.terms_accepted) await showDialog(doc, "accept"); // the version was bumped meanwhile
      else if (!doc.terms_session_ack) await showDialog(doc, "reminder");
    } catch {
      schedule();
    } finally {
      running = false;
    }
  }
  function onVisible(): void {
    if (document.visibilityState === "visible") void attempt();
  }
  window.addEventListener("online", attempt);
  document.addEventListener("visibilitychange", onVisible);
  schedule();
}

/** Acceptance is required but the text can't be loaded: say so, offer
 * Retry (and log out), never a dead end. Resolves once accepted. */
function showUnavailable(): Promise<void> {
  return new Promise<void>((resolve) => {
    showBootError({
      title: "Terms of use",
      message: `Before you can use WRAP you need to accept the terms of use, and they couldn't be loaded. ${offlineText()}`,
      retry: async () => {
        const doc = await fetchTerms(2); // throws: the screen stays, with "still can't reach"
        hideBootError();
        if (!doc.terms_accepted) await showDialog(doc, "accept");
        else if (!doc.terms_session_ack) await showDialog(doc, "reminder");
        resolve();
      },
      secondary: { label: "Log out", run: () => void logout().finally(() => window.location.reload()) },
    });
  });
}

/** Before the app boots (main.ts): the blocking accept dialog if the current
 * version isn't accepted, else the once-per-login reminder. Resolves when
 * the user may carry on. A failed fetch never blocks a user who has already
 * accepted (bootRetry.termsBootAction). */
export async function ensureTermsAccepted(): Promise<void> {
  const user = getState().currentUser;
  if (!user) return;
  if (user.terms_accepted && user.terms_session_ack) return;
  let doc: TermsDoc | null = null;
  try {
    // an accepted user is only waiting for the reminder: don't hold the boot long
    doc = await fetchTerms(user.terms_accepted ? 3 : 4);
  } catch {
    doc = null;
  }
  switch (termsBootAction(user, doc)) {
    case "accept":
      await showDialog(doc as TermsDoc, "accept");
      return;
    case "reminder":
      await showDialog(doc as TermsDoc, "reminder");
      return;
    case "defer-reminder":
      deferReminder();
      return;
    case "unavailable":
      await showUnavailable();
      return;
    case "none":
      return;
  }
}

/** Read-only view (sidebar footer link). */
export async function openTermsViewer(): Promise<void> {
  try {
    await showDialog(await fetchTerms(2), "view");
  } catch {
    // nothing sensible to show; the link just does nothing
  }
}

let requiredShowing = false;

/** Wire the sidebar footer link and the mid-session handler: a 403
 * terms_not_accepted from any request (the version was bumped while the
 * app was open) brings back the accept dialog, then reloads. */
export function initTerms(): void {
  document.getElementById("terms-link")?.addEventListener("click", () => void openTermsViewer());
  setTermsRequiredHandler(() => {
    if (requiredShowing) return;
    requiredShowing = true;
    void fetchTerms()
      .then((doc) => (doc.terms_accepted ? undefined : showDialog(doc, "accept")))
      .then(() => window.location.reload())
      .catch(() => {
        requiredShowing = false;
      });
  });
}
